import { useQuery } from '@tanstack/react-query';
import { simulationService } from '~/services';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { adaptFileUploadListItem, type UnifiedSimulationUpload } from './uploadAdapter';
import type { SimulationServiceV1SimulationFileUploadsListData } from '@shipium-corp/shipium-ts-types';

type FileUploadListItem = SimulationServiceV1SimulationFileUploadsListData[number];

/**
 * Merges uploads from the legacy upload API and the new file-upload API
 * into a single sorted, deduplicated list of UnifiedSimulationUpload.
 *
 * Enables 10-second polling when any new upload is still validating/pending.
 */
export function useMergedUploads(partnerId: string, isShipiumView = false) {
  const legacyQuery = useQuery({
    ...useQueryCreator(simulationService.uploads.search, { args: [partnerId] }),
    enabled: !!partnerId,
  });

  const newUploadsQuery = useQuery({
    ...useQueryCreator(simulationService.fileUploads.list, { args: [] }),
    enabled: !!partnerId,
    refetchInterval: (data) => {
      const items = (data ?? []).flat();
      const stillProcessing = items.some((item) => item.status === 'pending' || item.status === 'validating');
      return stillProcessing ? 10_000 : false;
    },
  });

  // The service type is double-wrapped as {..}[][] due to the generated list type;
  // flatten to get the actual item array.
  const newUploadItems: FileUploadListItem[] = (newUploadsQuery.data ?? []).flat();

  const merged: UnifiedSimulationUpload[] = (() => {
    const legacyUploads: UnifiedSimulationUpload[] = (legacyQuery.data ?? []).map((u) => ({
      ...u,
      _uploadSource: { type: 'legacy' as const },
    }));

    const newUploads: UnifiedSimulationUpload[] = newUploadItems.map(adaptFileUploadListItem);

    // Build a set of s3Keys that the new system marks as not visible to partners.
    // Used to filter out the corresponding legacy record too (dual-write dedup).
    const hiddenS3Keys = new Set<string>();
    if (!isShipiumView) {
      for (const upload of newUploads) {
        if (upload._visibleToPartner === false) {
          hiddenS3Keys.add(upload.s3Key);
        }
      }
    }

    // Deduplicate by s3Key — prefer new upload if there's a collision
    const byS3Key = new Map<string, UnifiedSimulationUpload>();
    for (const upload of legacyUploads) {
      if (!hiddenS3Keys.has(upload.s3Key)) {
        byS3Key.set(upload.s3Key, upload);
      }
    }
    for (const upload of newUploads) {
      if (!hiddenS3Keys.has(upload.s3Key)) {
        byS3Key.set(upload.s3Key, upload);
      }
    }

    // Sort by createdAt descending (newest first)
    return Array.from(byS3Key.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  })();

  return {
    data: merged,
    isLoading: legacyQuery.isLoading || newUploadsQuery.isLoading,
    isError: legacyQuery.isError || newUploadsQuery.isError,
  };
}
